/**
 * Card - Componente de visualización de pedido enviado en mobile
 *
 * Este componente muestra la información de un pedido enviado en formato de tarjeta,
 * respetando las columnas seleccionadas por el usuario a través del ColumnSelector.
 * La columna 'ref' (número de pedido) es fija y siempre se muestra.
 */
import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { faBagShopping, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { statusColorMap } from '../../config/orderStatusConfig';
import { ProductLine, ProductsModal, ProductsModalRef } from '../modals';

export interface CardProps {
  item: any; // Order
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  selectedColumns?: string[];
}

export const Card = React.memo(function Card({
  item: order,
  className = '',
  showActions = true,
  selectedColumns,
}: CardProps) {
  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true; // Si no hay selectedColumns, mostrar todo
    return selectedColumns.includes(columnId);
  };

  {
    /* Estado logístico del envío - Shipping logistic status */
  }
  const statusMapped = order.shipment?.status_mapped;
  const statusColor = statusMapped
    ? statusColorMap[statusMapped.id] || { bg: 'bg-card-border', text: 'text-text' }
    : { bg: 'bg-card-border', text: 'text-text' };

  const productsModalRef = useRef<ProductsModalRef>(null);
  const [modalData, setModalData] = useState<{
    orderNumber: string;
    stockMessage: string;
    products: ProductLine[];
    subtotal: number;
    shipping: number;
    discount: number;
    total: number;
  } | null>(null);

  const handleOpenProductsModal = () => {
    setModalData({
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      products: order.lines.map((line: any) => ({
        name: line.name,
        image: line.product?.image_hash || '',
        badge: line.product?.code || '',
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce((acc: number, line: any) => acc + line.qty * line.amount, 0),
      shipping: order.total_shipping,
      discount: order.total_discount,
      total: order.total,
    });
    setTimeout(() => productsModalRef.current?.open(), 0);
  };

  const hasValidId = order.id && order.id > 0;

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Header con número de pedido, estado y botón de ver (ref siempre visible, status condicional) */}
      <div className="flex items-center gap-2 mb-3 justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-base">{order.ref}</span>
          {isColumnVisible('status') && (
            <Badge className={`text-xs ${statusColor.bg} ${statusColor.text}`}>
              {statusMapped ? statusMapped.name : 'Sin estado logístico'}
            </Badge>
          )}
        </div>
        {showActions && hasValidId && (
          <Link
            to={`/orders/view/${order.id}`}
            className="text-complementary-text hover:text-text transition-colors inline-flex items-center justify-center p-2"
            title="Ver pedido"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Información de la tienda - Solo si la columna está visible */}
      {isColumnVisible('shop') && (
        <div className="flex items-center gap-2 mb-2">
          <ShopComponent name={order.shop.name} src={order.shop.image_hash} />
        </div>
      )}

      {/* Información del courier - Solo si la columna está visible */}
      {isColumnVisible('courier') && order.shipment.courier && (
        <div className="flex items-center gap-2 mb-2">
          <ShopComponent name={order.shipment.courier?.name} src={order.shipment.courier?.image_hash} />
        </div>
      )}

      {/* Información de productos - Solo si la columna está visible */}
      {isColumnVisible('products') && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 mt-1 ml-auto">
            <button
              type="button"
              className="relative inline-flex items-center gap-1 focus:outline-none hover:opacity-70"
              onClick={handleOpenProductsModal}
              title="Ver productos"
            >
              <span className="relative inline-block">
                <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
                <span
                  className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${order.lines?.[0]?.qty > 0 ? 'bg-success' : 'bg-error'}`}
                ></span>
              </span>
              <span className="text-sm">{order.lines.length}</span>
            </button>
          </div>
        </div>
      )}

      {modalData && (
        <ProductsModal
          ref={productsModalRef}
          orderNumber={modalData.orderNumber}
          stockMessage={modalData.stockMessage}
          products={modalData.products}
          subtotal={modalData.subtotal}
          shipping={modalData.shipping}
          discount={modalData.discount}
          total={modalData.total}
        />
      )}
    </div>
  );
});
